import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { isEqual, keyBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Spacer, Typography } from '@emburse/ui-core';
import { getExpenseBucketsForUser } from '@Actions/expenseBucketsWithTagsActions';
import { CategorySelector } from '../Fields/CategorySelector';
import { ExpenseTagBucketsFormControls } from '../Fields/ExpenseTagBucketsFormControls';
// Check if selected tag bucket is available for selected user
// If if is not available then remove if from the state
const getUpdatedBucketSelections = (expenseTagsBucketsForUser, selectedExpenseTagsByBucketId, textExpenseTagsByBucketId) => {
    const bucketsById = keyBy(expenseTagsBucketsForUser, 'id');
    const updatedDropdownSelections = Object.entries(selectedExpenseTagsByBucketId).reduce((selections, [key, value]) => {
        return bucketsById[key] ? Object.assign(Object.assign({}, selections), { [key]: value }) : selections;
    }, {});
    const updatedInputSelections = Object.entries(textExpenseTagsByBucketId).reduce((selections, [key, value]) => {
        return bucketsById[key] ? Object.assign(Object.assign({}, selections), { [key]: value }) : selections;
    }, {});
    if (!isEqual(selectedExpenseTagsByBucketId, updatedDropdownSelections) || !isEqual(textExpenseTagsByBucketId, updatedInputSelections)) {
        return {
            selectedExpenseTagsByBucketId: updatedDropdownSelections,
            textExpenseTagsByBucketId: updatedInputSelections
        };
    }
};
export const ExpenseFieldsFormBase = ({ cardsIssuingExpenseFieldsFeatureFlag, assigneeUserId, assigneeEmail, selectedCategoryId, selectedExpenseTagsByBucketId, textExpenseTagsByBucketId, activeExpenseBucketsWithActiveTags, disabled, onChange }) => {
    const hasNoUserSelected = window.FEATURES.accelerated_card_issuing ? !assigneeUserId && !assigneeEmail : !assigneeUserId;
    const shouldNotRender = !cardsIssuingExpenseFieldsFeatureFlag || hasNoUserSelected;
    const { expenseBucketsByUser, expenseBucketsWithTagsLoading } = useSelector((state) => state.expenseBucketsWithTags);
    const { categoriesByUser, loading: categoriesLoading } = useSelector((state) => state.categories);
    const loading = expenseBucketsWithTagsLoading || categoriesLoading;
    const dispatch = useDispatch();
    React.useEffect(() => {
        if ((!shouldNotRender && !expenseBucketsByUser[assigneeUserId]) || !categoriesByUser[assigneeUserId]) {
            dispatch(getExpenseBucketsForUser(assigneeUserId, Object.values(selectedExpenseTagsByBucketId)));
        }
    }, [assigneeUserId, shouldNotRender]);
    React.useEffect(() => {
        if (!shouldNotRender && expenseBucketsByUser[assigneeUserId]) {
            const updatedBucketSelections = getUpdatedBucketSelections(expenseBucketsByUser[assigneeUserId], selectedExpenseTagsByBucketId, textExpenseTagsByBucketId);
            if (updatedBucketSelections) {
                onChange(updatedBucketSelections);
            }
        }
    }, [expenseBucketsByUser, shouldNotRender]);
    // Whenever we finish loading, remember the previous assignee ID
    // The assigneeUserId prop changes right when you select a new assignee, but the buckets/categories in the store aren't updated until an async fetch is complete
    // Therefore, there is a brief period in between where we have a new assigneeUserId, but expenseBucketsByUser[assigneeUserId] is not loaded yet
    // During this brief period, keep showing the buckets/categories of the previousAssigneeId -- this prevents the fields from constantly disappearing and reappearing
    const [previousAssigneeId, setPreviousAssigneeId] = React.useState(null);
    React.useEffect(() => {
        if (!shouldNotRender && !loading) {
            setPreviousAssigneeId(assigneeUserId);
        }
    }, [loading, shouldNotRender]);
    if (shouldNotRender && !disabled) {
        return null;
    }
    const shouldUsePreviousAssigneeId = (loading && previousAssigneeId) || disabled;
    const assigneeIdToUse = shouldUsePreviousAssigneeId ? previousAssigneeId : assigneeUserId;
    if ((loading && !previousAssigneeId) || !expenseBucketsByUser[assigneeIdToUse] || !categoriesByUser[assigneeIdToUse]) {
        return null;
    }
    const categoryOptions = categoriesByUser[assigneeIdToUse];
    const expenseTagsBucketsForUser = expenseBucketsByUser[assigneeIdToUse];
    const handleExpenseTagChange = (bucketId, val) => {
        const bucket = expenseTagsBucketsForUser.find((etb) => etb.id === bucketId);
        let selections, updatedData;
        if (bucket.input_type === 'text') {
            selections = Object.assign({}, textExpenseTagsByBucketId);
            selections[bucketId] = val;
            updatedData = { textExpenseTagsByBucketId: selections };
        }
        else {
            selections = Object.assign({}, selectedExpenseTagsByBucketId);
            selections[bucketId] = val;
            updatedData = { selectedExpenseTagsByBucketId: selections };
        }
        if (!val) {
            delete selections[bucketId];
        }
        onChange(updatedData);
    };
    return (_jsxs(Grid, Object.assign({ container: true, spacing: 2 }, { children: [_jsxs(Grid, Object.assign({ item: true, xs: 12 }, { children: [_jsx(Spacer, { size: 14 }), _jsx(Typography, Object.assign({ variant: "subtitle1", className: disabled ? 'disabled' : '' }, { children: "Additional Company Attributes" }))] })), _jsx(Grid, Object.assign({ className: "category-picker", item: true, xs: 12 }, { children: _jsx(CategorySelector, { loading: loading, categories: categoryOptions, selectedCategoryId: selectedCategoryId, onCategorySelect: (category) => onChange({ selectedCategoryId: category === null || category === void 0 ? void 0 : category.id }), disabled: disabled }) })), _jsx(ExpenseTagBucketsFormControls, { expenseTagsBucketsForUser: expenseTagsBucketsForUser, selectedExpenseTagsByBucketId: selectedExpenseTagsByBucketId, textExpenseTagsByBucketId: textExpenseTagsByBucketId, activeExpenseBucketsWithActiveTags: activeExpenseBucketsWithActiveTags, onExpenseTagChange: handleExpenseTagChange, disabled: disabled })] })));
};
export const ExpenseFieldsForm = React.memo(ExpenseFieldsFormBase);
